/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import moment from 'moment';
import * as R from 'ramda';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, FormFeedback, FormGroup, FormText, Input, Label, Table } from 'reactstrap';
import { playlistAdminPath } from '..';
import { withTitle } from '../../../reactutils/withTitle';
import { Album, AlbumSong, MusicbrainzMedia, MusicbrainzRelease, MusicbrainzTrack } from '../../../services/playlist';
import { ButtonWithConfirmation } from '../../common/ButtonWithConfirmation';
import { FileUpload } from '../../common/FileUpload';
import { LoadingIndicatorContent, LoadingIndicatorSpinner } from '../../common/LoadingIndicatorSpinner';
import { Section, SectionHeading } from '../../common/Section';
import { usePathSegment } from '../../SysAdminApp/CacheStatisticsComponent';
import { getSystemConfig } from '../../System/systemConfig';
import AlbumSongList from '../AlbumSongList';
import {
    albumAttachCoverArtImage,
    albumAttachMusicbrainzCoverArt,
    albumDelete,
    albumDeleteCover,
    albumImportMusicbrainzRelease,
    albumLoadMusicbrainz,
    albumLoadMusicbrainzDetails,
    albumRemoveDuplicatedSongs,
    albumSetMusicbrainz,
    loadAlbum,
    saveAlbum
} from './actions';
import {
    albumEditAlbumNameSelector,
    albumEditAlbumSelector,
    albumEditAlbumSongsSelector,
    albumEditMusicbrainzLoadingSelector,
    albumEditMusicbrainzSelector,
    albumMusicbrainzReleaseByIdSelector
} from './selectors';

export interface AlbumEditProps  {
    album: Album;
    albumSongs: AlbumSong[];

    uploadedImageId: string;

    onAlbumIdChanged: (albumId: string) => void;
    onSave: (album: Album) => void;
    onImageUploaded: (f: File) => void;
}

const AlbumEdit: React.FC<{}> = () => {
    const [albumId] = usePathSegment(3);
    const [name, setName] = React.useState('');
    const [imageId, setImageId] = React.useState<string | undefined>('');
    const [complete, setComplete] = React.useState(false);
    const [editMode, setEditMode] = React.useState(false);

    const albumSongs = useSelector(albumEditAlbumSongsSelector);
    const album = useSelector(albumEditAlbumSelector);
    const musicbrainz = useSelector(albumEditMusicbrainzSelector);
    const musicbrainzIsLoading = useSelector(albumEditMusicbrainzLoadingSelector);

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (albumId) {
            dispatch(loadAlbum(albumId));
            dispatch(albumSetMusicbrainz([]));
        }
    }, [dispatch, albumId]);

    React.useEffect(() => {
        if (album) {
            setName(album.name);
            setImageId(album.imageId);
            setComplete(album.complete);
        }
    }, [album]);

    const onUpload = (imageId: string | undefined) => {
        if (imageId) {
            dispatch(albumAttachCoverArtImage({ albumId, imageId }));
        }
    };

    const onDelete = () => {
        dispatch(albumDelete(albumId));
    };

    const onEdit = () => {
        setEditMode(true);
    };
    const onSave = () => {
        dispatch(saveAlbum({ ...album, name, complete }));
        setEditMode(false);
    };

    const onCancel = () => {
        setEditMode(false);
        dispatch(loadAlbum(albumId));
    };

    const onLoadMusicbrainz = () => {
        dispatch(albumLoadMusicbrainz(albumId));
    };

    const onDeleteCover = () => {
        dispatch(albumDeleteCover(albumId));
    };

    const onRemoveDuplicatedSongs = () => {
        dispatch(albumRemoveDuplicatedSongs(albumId));
    };

    const onImportMusicbrainzRelease = (releaseId: string) => {
        dispatch(albumImportMusicbrainzRelease({ albumId, releaseId }));
    };

    const onAttachMusicbrainzCoverArt = (url?: string) => {
        if (url) {
            dispatch(albumAttachMusicbrainzCoverArt({ albumId, url }));
        }
    };

    return (
        <>
            <Section>
                <SectionHeading>Album bearbeiten</SectionHeading>

                <form>
                    <FormGroup>
                        <Label>Titel:</Label>
                        <Input
                            type="text"
                            name="name"
                            value={name}
                            onChange={e => setName(e.currentTarget.value)}
                            readOnly={!editMode}
                        />
                        <FormFeedback />
                        <FormText>Album Titel, max. 60 Zeichen</FormText>
                    </FormGroup>

                    <FormGroup check={true}>
                        <Input
                            type="checkbox"
                            name="complete"
                            checked={complete}
                            onChange={e => setComplete(e.currentTarget.checked)}
                            disabled={!editMode}
                        />
                        <Label>Complete</Label>

                        <FormFeedback />
                        <FormText>Sind die Songs auf dem Album vollständig?</FormText>
                    </FormGroup>

                    <div className="mt-2">
                        {editMode && (
                            <>
                                <Button onClick={onSave} color="success" className="me-2">
                                    Speichern
                                </Button>
                                <Button onClick={onCancel} className="me-2">
                                    Abbrechen
                                </Button>
                            </>
                        )}
                        {!editMode && (
                            <>
                                <Button onClick={onEdit} color="success" className="me-2">
                                    Bearbeiten
                                </Button>
                                <ButtonWithConfirmation
                                    onOK={onDelete}
                                    message="Sind Sie sicher, dass Sie das aktuelle Album löschen wollen? Diese Aktion kann nicht rückgängig gemacht werden!"
                                    title="Album löschen"
                                    color="danger"
                                    className="me-2"
                                >
                                    Album löschen
                                </ButtonWithConfirmation>
                            </>
                        )}
                    </div>
                </form>
            </Section>

            <Section>
                <SectionHeading>Album Cover</SectionHeading>
                <div className="d-flex flex-row justify-content-between mt-3 flex-wrap">
                    <div className="col-9 my-2">
                        <FileUpload imageUploaded={onUpload} />
                    </div>
                    {imageId && (
                        <FormGroup className="col-3 my-2">
                            <img className="img-fluid" src={getSystemConfig().imageUrl(imageId)} alt="" />
                        </FormGroup>
                    )}
                </div>{' '}
                <ButtonWithConfirmation
                    onOK={onDeleteCover}
                    message="Sind Sie sicher, dass Sie das aktuelle Album-Cover löschen wollen?"
                    title="Album-Cover löschen"
                    color="danger"
                    className="me-2"
                >
                    Album-Cover löschen
                </ButtonWithConfirmation>
            </Section>
            <Section>
                <SectionHeading>Songs auf dem Album</SectionHeading>
                <AlbumSongList albumSongs={albumSongs} />
                <ButtonWithConfirmation
                    color="danger"
                    onOK={onRemoveDuplicatedSongs}
                    title="Doppelte Songs löschen"
                    message="Sind Sie sicher, dass Sie alle doppelten Songs vom Album löschen wollen?"
                >
                    doppelte Songs löschen
                </ButtonWithConfirmation>
            </Section>

            <Section>
                <SectionHeading>MusicBrainz Daten</SectionHeading>
                {musicbrainz.length === 0 && (
                    <Button onClick={onLoadMusicbrainz} color="success">
                        MusicBrainz Daten laden
                    </Button>
                )}
                {musicbrainzIsLoading && (
                    <div className="d-flex justify-content-center">
                        <LoadingIndicatorSpinner />
                    </div>
                )}
                {musicbrainz && (
                    <Table striped={true}>
                        <tbody>
                            {musicbrainz.map(m => (
                                <MusicBrainzReleaseRow
                                    key={m.id}
                                    release={m}
                                    loadedAlbum={album}
                                    onAttachMusicbrainzCoverArt={url => onAttachMusicbrainzCoverArt(url)}
                                    onImportMusicbrainzRelease={releaseId => onImportMusicbrainzRelease(releaseId)}
                                />
                            ))}
                        </tbody>
                    </Table>
                )}
            </Section>
        </>
    );
};

interface MusicBrainzReleaseRowProps {
    release: MusicbrainzRelease;
    loadedAlbum: Album;
    onImportMusicbrainzRelease: (releaseId: string) => void;
    onAttachMusicbrainzCoverArt: (coverArtUrl?: string) => void;
}

const MusicBrainzReleaseRow: React.FC<MusicBrainzReleaseRowProps> = props => {
    const releaseWithTracks = useSelector(albumMusicbrainzReleaseByIdSelector(props?.release?.id));

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (props?.release?.id && props?.release?.id !== releaseWithTracks?.id) {
            console.info('loading release ' + props.release.id);
            dispatch(albumLoadMusicbrainzDetails(props.release.id));
        }
    }, [dispatch, props, releaseWithTracks]);

    return (
        <tr key={props.release.id} className="d-flex">
            <td className="col-4">{props.release.title}</td>
            <td className="col-2">{props.release.status}</td>
            <td className="col-4">
                {props.release.thumbnailUrl && (
                    <img className="img-fluid" src={props.release.thumbnailUrl} alt={props.release.title} />
                )}
                <MusicBrainzTrackList release={releaseWithTracks} />
            </td>
            <td className="col-2">
                {props.release.coverArtUrl && (
                    <>
                        <ButtonWithConfirmation
                            title="Album-Cover ersetzten"
                            message="Sind Sie sicher, dass Sie das bestehende Album-Cover ersetzen wollen?"
                            onOK={() => props.onAttachMusicbrainzCoverArt(props.release.coverArtUrl)}
                            size="sm"
                            className="m-1"
                        >
                            Attach Cover
                        </ButtonWithConfirmation>
                    </>
                )}
                <ButtonWithConfirmation
                    onOK={() => props.onImportMusicbrainzRelease(props.release.id)}
                    title="Musicbrainz Album importieren"
                    message="Sind Sie sicher, dass Sie die Song vom Album von Musicbrainz importieren wollen?"
                    color="success"
                    className="m-1"
                    size="sm"
                >
                    Import
                </ButtonWithConfirmation>
            </td>
        </tr>
    );
};

const MusicBrainzTrackList: React.FC<{ release: MusicbrainzRelease }> = props => {
    const renderMedia = (media: MusicbrainzMedia, index: number) => {
        return (
            <React.Fragment key={index}>
                <tr>
                    <td className="m-0 p-0">
                        {media.format}&nbsp;{media.position}
                    </td>
                </tr>
                {media.tracks && media.tracks.map(track => renderTrack(track))}
            </React.Fragment>
        );
    };

    const renderTrack = (track: MusicbrainzTrack) => {
        return (
            <tr key={track.id}>
                <td className="m-0 p-0">{track.position}</td>
                <td className="m-0 p-0">{track.title}</td>
                <td className="m-0 p-0">{track.artist}</td>
                <td className="m-0 p-0">{moment.utc(track.length).format('mm:ss')}</td>
            </tr>
        );
    };
    return (
        <>
            <small>
                <LoadingIndicatorContent isLoading={!props?.release?.media}>
                    <Table className="m-0 p-0" striped={false}>
                        <tbody>
                            {props?.release?.media &&
                                props.release.media.map((media, index) => renderMedia(media, index))}
                        </tbody>
                    </Table>
                </LoadingIndicatorContent>
            </small>
        </>
    );
};

export const albumEditPath = (albId: string) => playlistAdminPath() + `/album/${albId}`;

export default R.compose(withTitle('Album', albumEditAlbumNameSelector))(AlbumEdit);
